import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import {
  ArrowLeft,
  Star,
  Camera,
  ClipboardCheck,
  DollarSign,
  Award,
  AlertTriangle,
  Clock,
  AlertCircle,
  Home,
  ChevronRight,
} from 'lucide-react';

interface VisitSummaryData {
  id: string;
  store_id: string;
  store_name: string;
  check_in_at: string;
  check_out_at: string;
  status: string;
  perfect_store_score?: number;
  photos_count: number;
  checklist_completed: boolean;
  prices_collected: number;
  extra_points: number;
  ruptures_count: number;
}

function formatDuration(checkIn: string, checkOut: string): string {
  const start = new Date(checkIn).getTime();
  const end = new Date(checkOut).getTime();
  const diffMs = end - start;

  if (diffMs < 0) return '--';

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  }
  return `${minutes}min`;
}

function ScoreGauge({ score }: { score: number }) {
  const clampedScore = Math.min(100, Math.max(0, score));
  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (clampedScore / 100) * circumference;

  let color = 'text-red-500';
  if (clampedScore >= 80) color = 'text-green-500';
  else if (clampedScore >= 60) color = 'text-yellow-500';
  else if (clampedScore >= 40) color = 'text-orange-500';

  return (
    <div className="relative flex items-center justify-center w-32 h-32 mx-auto">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          className="text-gray-100"
        />
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className={color}
          style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-gray-900">{clampedScore}</span>
        <span className="text-xs text-gray-500 -mt-1">pontos</span>
      </div>
    </div>
  );
}

export default function VisitSummary({ visitId }: { visitId: string }) {
  const [, setLocation] = useLocation();

  // Fetch visit summary
  const { data: visit, isLoading, error } = useQuery<VisitSummaryData>({
    queryKey: ['visit', 'summary', visitId],
    queryFn: () => api.get(`/visits/${visitId}`).then(res => res.data.data),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando resumo...</p>
        </div>
      </div>
    );
  }

  if (error || !visit) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Erro ao carregar resumo</h2>
        <p className="text-gray-600 mb-6 text-center">
          Nao foi possivel carregar os dados da visita
        </p>
        <button
          onClick={() => setLocation('/route')}
          className="px-6 py-3 bg-primary text-white rounded-lg font-semibold"
        >
          Voltar ao roteiro
        </button>
      </div>
    );
  }

  const duration = visit.check_in_at && visit.check_out_at
    ? formatDuration(visit.check_in_at, visit.check_out_at)
    : '--';

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation('/route')}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Resumo da Visita</h1>
            {visit.store_name && (
              <p className="text-sm opacity-80">{visit.store_name}</p>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4 pb-28 space-y-4">
        {/* Perfect Store Score */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Star className="h-5 w-5 text-primary" />
            <h2 className="font-semibold text-gray-900">Score Loja Perfeita</h2>
          </div>
          {visit.perfect_store_score !== undefined && visit.perfect_store_score !== null ? (
            <ScoreGauge score={visit.perfect_store_score} />
          ) : (
            <div className="text-center py-4">
              <p className="text-2xl font-bold text-gray-400">--</p>
              <p className="text-sm text-gray-500 mt-1">Score nao disponivel</p>
            </div>
          )}
        </div>

        {/* Summary Cards Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Photos */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 rounded-full bg-blue-100">
                <Camera className="h-4 w-4 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{visit.photos_count ?? 0}</p>
            <p className="text-xs text-gray-500 mt-1">Fotos Enviadas</p>
          </div>

          {/* Checklist */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 rounded-full bg-purple-100">
                <ClipboardCheck className="h-4 w-4 text-purple-600" />
              </div>
            </div>
            <div className="mt-1">
              {visit.checklist_completed ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                  Completo
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full">
                  Incompleto
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">Checklist</p>
          </div>

          {/* Prices Collected */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 rounded-full bg-green-100">
                <DollarSign className="h-4 w-4 text-green-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{visit.prices_collected ?? 0}</p>
            <p className="text-xs text-gray-500 mt-1">Precos Coletados</p>
          </div>

          {/* Extra Points */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 rounded-full bg-amber-100">
                <Award className="h-4 w-4 text-amber-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{visit.extra_points ?? 0}</p>
            <p className="text-xs text-gray-500 mt-1">Pontos Extras</p>
          </div>

          {/* Ruptures */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 rounded-full bg-red-100">
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{visit.ruptures_count ?? 0}</p>
            <p className="text-xs text-gray-500 mt-1">Rupturas</p>
          </div>

          {/* Total Time */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 rounded-full bg-indigo-100">
                <Clock className="h-4 w-4 text-indigo-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{duration}</p>
            <p className="text-xs text-gray-500 mt-1">Tempo Total</p>
          </div>
        </div>
      </main>

      {/* Fixed Bottom Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 space-y-2">
        <button
          onClick={() => setLocation('/route')}
          className="w-full flex items-center justify-center gap-2 py-4 bg-primary text-white rounded-xl font-semibold active:bg-primary/90"
        >
          <ChevronRight className="h-5 w-5" />
          <span>Proxima Loja</span>
        </button>
        <button
          onClick={() => setLocation('/')}
          className="w-full flex items-center justify-center gap-2 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold active:bg-gray-200"
        >
          <Home className="h-5 w-5" />
          <span>Voltar ao Inicio</span>
        </button>
      </div>
    </div>
  );
}
